import { GameSettings, SessionState } from '../types';
import { getApiUrl } from './apiBase';

const API_URL = getApiUrl();

/** Разбирает JSON-ответ сессии или бросает ошибку. */
async function readSession(response: Response): Promise<SessionState> {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Session request failed');
  }
  return data as SessionState;
}

/** Загружает текущую сессию. */
export async function fetchSession(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session`, { credentials: 'include' });
  return readSession(response);
}

/** Запускает пакет как новую игру. */
export async function startPackage(packageId: string): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ packageId }),
  });
  return readSession(response);
}

/** Запускает трек по номеру. */
export async function playNumber(number: number): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/play`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ number }),
  });
  return readSession(response);
}

/** Сообщает, что трек доиграл до конца. */
export async function trackEnded(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/ended`, {
    method: 'POST',
    credentials: 'include',
  });
  return readSession(response);
}
/** Останавливает текущий трек. */
export async function stopPlayback(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/stop`, {
    method: 'POST',
    credentials: 'include',
  });
  return readSession(response);
}

/** Снимает автостоп текущего трека. */
export async function dontStop(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/dont-stop`, {
    method: 'POST',
    credentials: 'include',
  });
  return readSession(response);
}

/** Дослушивает оборванный трек. */
export async function resumePaused(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/resume-paused`, {
    method: 'POST',
    credentials: 'include',
  });
  return readSession(response);
}

/** Включает или выключает скрытие сыгранных. */
export async function setHidePlayed(hidePlayed: boolean): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/hide-played`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ hidePlayed }),
  });
  return readSession(response);
}

/** Сбрасывает игру текущего пакета. */
export async function resetGame(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/reset`, {
    method: 'POST',
    credentials: 'include',
  });
  return readSession(response);
}

/** Завершает игру. */
export async function endGame(): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/end`, {
    method: 'POST',
    credentials: 'include',
  });
  return readSession(response);
}

/** Сохраняет настройки сессии. */
export async function updateSettings(settings: Partial<GameSettings>): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(settings),
  });
  return readSession(response);
}

/** Отправляет фактическую позицию аудио с ТВ. */
export async function reportPosition(currentTime: number): Promise<SessionState> {
  const response = await fetch(`${API_URL}/session/position`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ currentTime }),
  });
  return readSession(response);
}
